/* eslint-disable no-undef */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  Box,
  Divider,
  HStack,
  Heading,
  Link,
  SeeMore,
  Stack,
  Text,
} from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, useContext, useMemo, useState } from 'react';
import { EMPLOYMENT_CHOICES } from 'v4/lib/data';
import { PublicProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { formatCommaSeparated } from 'v4/lib/commons';
import { MyNetworkContext } from 'v4/components/network/context';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';
import { JobPlaceholderIcon } from '@mybridge/icons';

export const MyProfileExperience = forwardRef(({ ...props }, ref) => {
  const { experiences, publicProfile } = useContext(PublicProfileContext);
  const { isFriend } = useContext(MyNetworkContext);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((state) => state.userProfile);

  // Filter experiences based on audience visibility
  const visibleExperiences = useMemo(() => {
    return experiences?.filter(exp => 
      exp?.audience === 1 || (exp?.audience === 2 && isFriend(publicProfile))
    ) ?? [];
  }, [experiences, isFriend, publicProfile]);

  // Check if all experiences are private (audience 2 or 3) and user is not a friend
  const allExperiencesPrivate = useMemo(() => {
    return experiences?.length > 0 && 
           experiences?.every(exp => exp?.audience === 2 || exp?.audience === 3) &&
           !isFriend(publicProfile);
  }, [experiences, isFriend, publicProfile]);

  const groupedExperiences = useMemo(() => {
    const myExps = {};
    if (!Array.isArray(visibleExperiences)) return myExps;
    for (let me of visibleExperiences) {
      if (myExps[me?.company_name]) {
        myExps[me.company_name].push(me);
      } else {
        myExps[me.company_name] = [me];
      }
    }
    return myExps;
  }, [visibleExperiences]);

  // const calculateTotalExperience = (experiences) => {

  //   if (!Array.isArray(experiences) || experiences.length === 0) {
  //     return ''; // or return 0, based on your requirement
  //   }

  //   let totalMonths = 0;

  //   for (const exp of experiences) {
  //     if (exp.start_month && exp.start_year) {
  //       const startDate = moment().set('M', exp.start_month - 1).set('Y', exp.start_year);
  //       const endDate = exp.currently_working
  //         ? moment()
  //         : exp.end_month && exp.end_year
  //         ? moment().set('M', exp.end_month - 1).set('Y', exp.end_year)
  //         : null;

  //       if (endDate) {
  //         totalMonths += endDate.diff(startDate, 'months');
  //       }
  //     }
  //   }

  //   const years = Math.floor(totalMonths / 12);
  //   const months = totalMonths % 12;
  //   const totalExperience = years + months / 12;

  //   if (totalExperience === 0) {
  //     return ''; // No experience
  //   }

  //   const yearLabel = totalExperience < 1 ? 'year' : 'years';
  //   return `${totalExperience.toFixed(1)} ${yearLabel}`;
  // };

  /*
  const calculateTotalExperience = (experiences) => {
    if (!Array.isArray(experiences) || experiences.length === 0) {
      return null;
    }

    const companyName = experiences[0]?.company_name;
    if (!companyName) return null;

    const filteredExperiences = experiences.filter(
      exp => exp.company_name?.toLowerCase() === companyName.toLowerCase()
    );

    let totalMonths = 0;

    for (const exp of filteredExperiences) {
      if (exp.start_month && exp.start_year) {
        const startDate = moment()
          .set('M', exp.start_month - 1)
          .set('Y', exp.start_year);
        const endDate = exp.currently_working
          ? moment()
          : exp.end_month && exp.end_year
          ? moment()
              .set('M', exp.end_month - 1)
              .set('Y', exp.end_year)
          : null;

        if (endDate) {
          totalMonths += endDate.diff(startDate, 'months');
        }
      }
    }

    if (totalMonths === 0) {
      return null; 
    }

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    const yearLabel = years === 1 ? 'yr' : 'yrs';
    const monthLabel = months === 1 ? 'month' : 'months';

    if (years > 0 && months > 0) {
      return `${years} ${yearLabel} ${months} ${monthLabel}`;
    } else if (years > 0) {
      return `${years} ${yearLabel}`;
    } else if (months > 0) {
      return `${months} ${monthLabel}`;
    } else {
      return null;
    }
  };
  */

  const calculateTotalExperience = (experiences) => {
    if (!Array.isArray(experiences) || experiences.length === 0) {
      return null;
    }

    const intervals = [];
    for (const exp of experiences) {
      const startYear = parseInt(exp.start_year);
      const startMonth = parseInt(exp.start_month);
      
      if (startYear) {
        // Treat 0 or invalid months as January (0 in moment)
        const sMonth = Math.max(1, startMonth || 1);
        const startDate = moment([startYear, sMonth - 1, 1]).startOf('month');
        
        let endDate;
        if (exp.currently_working) {
          endDate = moment().endOf('month');
        } else {
          const endYear = parseInt(exp.end_year);
          const endMonth = parseInt(exp.end_month);
          if (endYear) {
            const eMonth = Math.max(1, endMonth || 12);
            endDate = moment([endYear, eMonth - 1, 1]).endOf('month');
          }
        }
        
        if (startDate && endDate && endDate.isAfter(startDate)) {
          intervals.push({ start: startDate, end: endDate });
        }
      }
    }

    if (intervals.length === 0) return null;

    // Merge overlapping intervals
    intervals.sort((a, b) => a.start.diff(b.start));
    const merged = [];
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i++) {
      const next = intervals[i];
      if (next.start.isBefore(current.end) || next.start.isSame(current.end)) {
        current.end = moment.max(current.end, next.end);
      } else {
        merged.push(current);
        current = next;
      }
    }
    merged.push(current);

    let totalMonths = 0;
    for (const interval of merged) {
      totalMonths += Math.max(0, interval.end.diff(interval.start, 'months')) + 1;
    }

    if (totalMonths === 0) return null;

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    const yearLabel = years === 1 ? 'yr' : 'yrs';
    const monthLabel = months === 1 ? 'month' : 'months';

    const parts = [];
    if (years > 0) parts.push(`${years} ${yearLabel}`);
    if (months > 0) parts.push(`${months} ${monthLabel}`);

    return parts.join(' ');
  };


  const Item = ({ exp, hasBar = false, isLast = false, hasTitle = false, expArr }) => {
    const totalExperience = calculateTotalExperience(expArr);
    return (
      <Stack spacing={0} pt={hasTitle ? 4 : 0} pos="relative">
        {hasBar && (
          <>
            <Box
              pos="absolute"
              w="20px"
              h="20px"
              top={4}
              left={'-40px'}
              bg="brandGray.300"
              borderRadius="full"
              zIndex={1}
            />
            <Box pos="absolute" w="1" top={4} bottom={-4} left={-8} bg="brandGray.300" />
          </>
        )}
        {isLast && (
          <Box
            pos="absolute"
            w="20px"
            h="20px"
            top={4}
            left={'-40px'}
            bg="brandGray.300"
            borderRadius="full"
            zIndex={1}
          />
        )}
        <HStack alignItems="flex-start">
          <Stack spacing={0} flex={1}>
            {hasTitle && exp?.title && <Text>{exp?.title}</Text>}
            <Box fontSize="sm">
              {exp?.start_month !== undefined && exp?.start_year && (
                <Text display="inline">
                  {moment([parseInt(exp.start_year), Math.max(0, (parseInt(exp.start_month) || 1) - 1), 1]).format('MMMM YYYY')}
                </Text>
              )}
              {exp?.currently_working ? (
                <Text display="inline"> - Present</Text>
              ) : exp?.end_month !== undefined && exp?.end_year ? (
                <Text display="inline">
                  {' - '}
                  {moment([parseInt(exp.end_year), Math.max(0, (parseInt(exp.end_month) || 1) - 1), 1]).format('MMMM YYYY')}
                </Text>
              ) : (
                ''
              )}
              {exp?.location?.length > 0 && (
                <>
                  <Text display="inline">&nbsp;·&nbsp;</Text>
                  <Text display="inline">{exp?.location}</Text>
                </>
              )}
            </Box>
            <HStack alignItems="center">
              <Box fontSize={["xs", "sm"]} color="#868585">
                {formatCommaSeparated(
                  [
                    exp?.industry,
                    EMPLOYMENT_CHOICES?.find(
                      (ec) => ec.value === exp?.employment_type
                    )?.label,
                  ],
                  ' · '
                )}
              </Box>
              {calculateTotalExperience(expArr) && (
                <>
                  <Text color="#868585" fontSize="2xl">
                    ·
                  </Text>
                  <Text fontSize={["xs", "sm"]} color="#868585">
                    {calculateTotalExperience(expArr)}
                  </Text>
                </>
              )}
            </HStack>
            <Stack py={2}>
              {exp?.desc && <SeeMore text={exp?.desc} />}
            </Stack>
          </Stack>
        </HStack>
      </Stack>
    );
  };

  // If no experience items at all, return empty fragment
  if (!experiences?.length) {
    return <></>;
  }

  const shouldShowHeading = !allExperiencesPrivate && Object.values(groupedExperiences ?? {}).length > 0;

  return (
    <Stack>
      {/* Only show heading if we're not showing the "No experience to show" message */}
      {shouldShowHeading && (
        <HStack alignItems="flex-start">
          <Stack flex={1}>
            <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
              {t('profileExperience.experience') || "Experience"}
            </Heading>
          </Stack>
        </HStack>
      )}
      
      {/* Show "No experience to show" message when all experience is private */}
      {allExperiencesPrivate ? (
        <HStack>
          <JobPlaceholderIcon height="20px" width="20px" color="#868585" />
          <Text color="#868585">
            {t('otherModules.noExperienceToShow') || "No experience to show"}
          </Text>
        </HStack>
      ) : Object.values(groupedExperiences ?? {}).length ? (
        <>
          <Stack pt={4} spacing={6}>
            {Object.values(groupedExperiences)
              .filter((_, ind) => showAll || ind < 3)
              .map((expArr, expIndex) => {
                const exp = expArr[0];
                return (
                  <Stack key={expIndex}>
                    <HStack alignItems="flex-start">
                      <Avatar as={Link} size="md" name={exp?.company_name} />
                      <Stack spacing={0} flex={1}>
                        <Box>
                          <Text display="inline" fontWeight="bold">
                            {exp?.company_name}
                          </Text>
                          {expArr.length <= 1 ? (
                            <Text display="inline">{' '}{exp?.title ? exp?.title : ''}</Text>
                          ) : (
                            <HStack alignItems="center">
                            <Box fontSize="sm" color="#868585">
                              {formatCommaSeparated(
                                [
                                  exp?.industry,
                                  EMPLOYMENT_CHOICES?.find(
                                    (ec) => ec.value === exp?.employment_type
                                  )?.label,
                                ],
                                ' · '
                              )}
                            </Box>
                            {calculateTotalExperience(expArr) && (
                              <>
                                <Text color="#868585" fontSize="2xl">
                                  ·
                                </Text>
                                <Text fontSize="sm" color="#868585">
                                  {calculateTotalExperience(expArr)}
                                </Text>
                              </>
                            )}
                          </HStack>
                          )}
                        </Box>
                        {expArr.length <= 1 ? (
                          <Item exp={exp} key={expIndex} expArr={expArr} />
                        ) : (
                          <Stack spacing={0} mt={6}>
                            {expArr.map((exp_, expIndex_) => (
                              <Item
                                hasTitle={true}
                                hasBar={expIndex_ < expArr.length - 1}
                                exp={exp_}
                                key={expIndex_}
                                isLast={expIndex_ === expArr.length - 1}
                                // expArr={expArr}
                              />
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </HStack>
                    {Object.values(groupedExperiences ?? {}).length > 1 && expIndex < Object.values(groupedExperiences ?? {}).length - 1 && (
                      <Divider />
                    )}
                  </Stack>
                );
              })}
          </Stack>
          {!showAll && Object.values(groupedExperiences).length > 3 && (
            <ShowAllLink onClick={() => setShowAll(true)} />
          )}
        </>
      ) : (
        <>
          {/* Only show "Show off your work journey" if it's the user's own profile */}
          {publicProfile?.id === userProfileInfo?.id ? (
            <HStack justifyContent="center">
              <Link onClick={(e) => experienceDisc.onOpen?.()}>
                {t('publicProfile.showOffWorkJourney') || "Show off your work journey! Click here to add experience"}
              </Link>
            </HStack>
          ) : (
            <HStack>
              <JobPlaceholderIcon height="18px" width="18px" color="#868585" />
              <Text color="#868585">
                {t('otherModules.noExperienceToShow') || "No experience to show"}
              </Text>
            </HStack>
          )}
        </>
      )}
    </Stack>
  );
});
